import {useLegacyGrpcRequestCallback} from '@aboutyou/data-fetcher';
import {CheckoutV1_logoutLazy} from '@aboutyou/grpc-clients/grips/protos/checkout/checkout_v1.grpc.lazy';
import {useTranslation} from '@aboutyou/i18n';
import {Log, LogoutEvent} from '@aboutyou/mlm';
import {useTrackLogout} from '@aboutyou/tracking';
import {useShowToast} from '@aboutyou/ui-components';
import {
  BroadcastUserDataToTabs,
  TADARIDA_LOGOUT_BROADCAST_KEY,
  useUser,
  useUserLoginType,
  useUserToken,
} from '@aboutyou/user';
import {doAsync} from '@aboutyou/utils';
import {useCallback} from 'react';
import {useHistory} from 'react-router';
import {useHelpCenterChatbot} from '../../../backend-services/HelpCenterChatbot/hooks/useHelpCenterChatbot';
import {useUpdateUserCacheOnLogout} from './useUpdateUserCacheOnLogout';

export const useLogoutHandler = () => {
  const {push} = useHistory();
  const {data: userData} = useUser();
  const loginType = useUserLoginType();
  const updateUserCache = useUpdateUserCacheOnLogout();
  const showToast = useShowToast();
  const {t} = useTranslation();
  const trackLogout = useTrackLogout();
  const logoutRequestCallback = useLegacyGrpcRequestCallback(
    CheckoutV1_logoutLazy,
  );
  const apiToken = useUserToken() ?? '';
  const {logOut: logOutHelpCenterChatbot} = useHelpCenterChatbot();

  const logoutClickHandler = useCallback(
    (type: 'userAction' | 'sessionValidator' = 'userAction') => {
      if (!apiToken) {
        Log.warn(
          '@aboutyou/logic-components',
          `${LogoutEvent} - Missing mobileApiToken`,
          {
            type,
          },
        );
      }

      if (userData) {
        logoutRequestCallback
          .mutateAsync({
            checkoutSession: null,
            customerSession: {
              authToken: apiToken,
            },
          })
          .then(() => {
            trackLogout({action: 'successful', label: loginType});
            Log.compare({
              actionName: LogoutEvent,
              success: true,
              attributes: {
                type,
                metadata: {},
              },
            });
            logOutHelpCenterChatbot();
          })
          .catch(error => {
            trackLogout({action: 'error', label: loginType});
            Log.compare({
              actionName: LogoutEvent,
              success: false,
              attributes: {
                type,
                metadata: {error},
              },
            });
          })
          .finally(() => {
            push('/');
            showToast({
              type: 'SUCCESS',
              content: t('You have been successfully logged out'),
            });
            updateUserCache();

            // Since there might be other tabs where use is still logged-in, we need to make other tabs logout as well when a user logs-out in the current tab.
            // This is done by broadcasting a message to all tabs.
            BroadcastUserDataToTabs.broadcastData({
              key: TADARIDA_LOGOUT_BROADCAST_KEY,
            });

            doAsync(() => {
              window.location.reload();
            });
          });
      }
    },
    [userData, apiToken],
  );

  return {logout: logoutClickHandler};
};
